import type { Task, TaskMode, TaskStatus } from '@simple-agent-manager/shared';

/**
 * Per-task metadata needed for rendering the session tree.
 *
 * This module used to own the legacy single-level `groupSessions()` grouping
 * helpers. Hierarchy rendering now lives in `sessionTree.ts` + `SessionTreeItem.tsx`,
 * which supports arbitrary depth. Only the task metadata shape remains here
 * because it's consumed by both the tree and (e.g.) session-state inference.
 */
export interface TaskInfo {
  id: string;
  title: string;
  parentTaskId: string | null;
  status: TaskStatus;
  blocked: boolean;
  /** What created this task (user, cron, webhook, mcp). */
  triggeredBy: string;
  /** Dispatch generation (0 = user-created, >0 = agent-dispatched). */
  dispatchDepth: number;
  /** Task execution mode: 'task' (autonomous) or 'conversation' (interactive). */
  taskMode: TaskMode;
}

/**
 * Build a map from taskId to TaskInfo from a tasks array.
 */
export function buildTaskInfoMap(tasks: Task[]): Map<string, TaskInfo> {
  const map = new Map<string, TaskInfo>();
  for (const t of tasks) {
    map.set(t.id, {
      id: t.id,
      title: t.title,
      parentTaskId: t.parentTaskId,
      status: t.status,
      blocked: t.blocked ?? false,
      triggeredBy: t.triggeredBy ?? 'user',
      dispatchDepth: t.dispatchDepth ?? 0,
      taskMode: t.taskMode,
    });
  }
  return map;
}
